import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Table,
  Tabs,
  Typography,
  Modal,
  Form,
  Input,
  Alert,
  Tag,
  Tooltip,
  Space,
  Popconfirm,
} from 'antd';
import {
  LogoutOutlined,
  UserAddOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  LockOutlined,
  MailOutlined,
  TeamOutlined,
  FileOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import ChangePassword from './ChangePassword';

const { Title, Text, Link } = Typography;

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

async function adminFetch(path, options = {}) {
  const token = await auth.currentUser.getIdToken();
  const res = await fetch(`${FUNCTIONS_BASE_URL}/${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ---------------------------------------------------------------------------
// Shared header / footer
// ---------------------------------------------------------------------------

function AdminHeader({ user }) {
  const handleSignOut = () => signOut(auth);
  return (
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: '1px solid var(--line)', padding: '0 24px', height: 56,
      background: 'var(--surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/assets/default-logo.svg" alt="TalentMapper" style={{ height: 24, width: 'auto' }} />
        <span style={{
          background: 'var(--almond)', color: 'var(--orange)',
          fontSize: 11, fontWeight: 700, padding: '3px 8px',
          borderRadius: 20, letterSpacing: '.4px', textTransform: 'uppercase',
        }}>Admin</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{user.email}</Text>
        <Tooltip title="Sign out">
          <Button icon={<LogoutOutlined />} onClick={handleSignOut} type="text" size="small">
            Sign out
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}

function LegalFooter() {
  return (
    <footer style={{
      borderTop: '1px solid #e8e8e8', padding: '24px', textAlign: 'center',
      fontSize: 11, color: '#888', lineHeight: 1.6,
    }}>
      <p style={{ margin: 0 }}>
        TalentMapper Limited · Company No. 14476153 · Registered in England &amp; Wales ·{' '}
        Lewis Building, Bull Street, Spaces, Birmingham, B4 6AF
      </p>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [form] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch('adminListUsers', { method: 'POST' });
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (uid, email) => {
    try {
      await adminFetch('adminDeleteUser', {
        method: 'POST',
        body: JSON.stringify({ uid }),
      });
      setUsers((prev) => prev.filter((u) => u.uid !== uid));
    } catch (err) {
      Modal.error({ title: 'Delete failed', content: err.message });
    }
  };

  const handleAddSubmit = async (values) => {
    setAddLoading(true);
    setAddError(null);
    try {
      const created = await adminFetch('adminCreateUser', {
        method: 'POST',
        body: JSON.stringify({ email: values.email, password: values.password }),
      });
      setUsers((prev) => [
        {
          uid: created.uid,
          email: created.email,
          displayName: '',
          disabled: false,
          createdAt: created.createdAt,
          lastSignIn: null,
        },
        ...prev,
      ]);
      form.resetFields();
      setAddModalOpen(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => <Text strong>{email}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'disabled',
      key: 'disabled',
      width: 100,
      render: (disabled) =>
        disabled ? <Tag color="red">Disabled</Tag> : <Tag color="green">Active</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: formatDate,
    },
    {
      title: 'Last sign-in',
      dataIndex: 'lastSignIn',
      key: 'lastSignIn',
      width: 180,
      render: formatDate,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title={`Delete ${record.email}?`}
          description="This will permanently remove their account and login access."
          onConfirm={() => handleDelete(record.uid, record.email)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <Tooltip title="Delete user">
            <Button icon={<DeleteOutlined />} danger type="text" size="small" />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{users.length} user{users.length !== 1 ? 's' : ''}</Text>
        <Space>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={fetchUsers} loading={loading} />
          </Tooltip>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => { setAddError(null); form.resetFields(); setAddModalOpen(true); }}
            style={{ background: '#F96700', borderColor: '#F96700' }}
          >
            Add user
          </Button>
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Table
        dataSource={users}
        columns={columns}
        rowKey="uid"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="small"
      />

      <Modal
        title="Add new user"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        {addError && <Alert message={addError} type="error" showIcon style={{ marginBottom: 16 }} />}
        <Form form={form} layout="vertical" onFinish={handleAddSubmit} requiredMark={false}>
          <Form.Item
            name="email"
            label="Email address"
            rules={[
              { required: true, message: 'Please enter an email address' },
              { type: 'email', message: 'Please enter a valid email address' },
            ]}
          >
            <Input prefix={<MailOutlined style={{ color: '#bbb' }} />} placeholder="client@company.com" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Temporary password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined style={{ color: '#bbb' }} />} placeholder="Min. 8 characters" size="large" />
          </Form.Item>
          <div style={{
            background: '#FFEECF', borderRadius: 8, padding: '10px 14px',
            fontSize: 12, color: '#212121', marginBottom: 16,
          }}>
            Share these credentials with the client in their onboarding email.
            They can only log in during an active browser session (no persistent auth).
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addLoading}
              style={{ background: '#F96700', borderColor: '#F96700' }}
            >
              Create user
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files tab
// ---------------------------------------------------------------------------

function FilesTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingPath, setDownloadingPath] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch('adminListFiles', { method: 'POST' });
      setFiles(data.files || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDownload = async (objectPath, fileName) => {
    setDownloadingPath(objectPath);
    try {
      const data = await adminFetch('adminGetDownloadUrl', {
        method: 'POST',
        body: JSON.stringify({ objectPath }),
      });
      const a = document.createElement('a');
      a.href = data.downloadUrl;
      a.download = fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } catch (err) {
      Modal.error({ title: 'Download failed', content: err.message });
    } finally {
      setDownloadingPath(null);
    }
  };

  const columns = [
    {
      title: 'File name',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (name) => (
        <span>
          <FileOutlined style={{ color: '#F96700', marginRight: 6 }} />
          <Text strong>{name}</Text>
        </span>
      ),
    },
    {
      title: 'Sender',
      dataIndex: 'senderName',
      key: 'senderName',
      width: 180,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Uploader email',
      dataIndex: 'uploaderEmail',
      key: 'uploaderEmail',
      width: 200,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 90,
      render: formatBytes,
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      width: 180,
      render: formatDate,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Button
          icon={<DownloadOutlined />}
          size="small"
          loading={downloadingPath === record.objectPath}
          onClick={() => handleDownload(record.objectPath, record.fileName)}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{files.length} file{files.length !== 1 ? 's' : ''} · deleted automatically after 60 days</Text>
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={fetchFiles} loading={loading} />
        </Tooltip>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Table
        dataSource={files}
        columns={columns}
        rowKey="objectPath"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="small"
        locale={{ emptyText: 'No files uploaded yet' }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security tab
// ---------------------------------------------------------------------------

const SECURITY_BLURB = `TalentMapper uses a secure, cloud-hosted file transfer system built on Google Cloud Platform and Firebase. When you upload a file, you authenticate via Firebase Email/Password authentication, which issues a short-lived ID token valid only for your current browser session. Your file is never routed through our application servers — instead, our Cloud Function validates your identity, generates a short-lived (15-minute) Google Cloud Storage signed URL, and your browser uploads directly to GCS over HTTPS (TLS 1.2+). Files are stored in a private GCS bucket with uniform bucket-level access; no object is publicly readable. Google Cloud Storage encrypts all data at rest using AES-256. Access is restricted to authorised TalentMapper staff via Firebase Admin SDK token verification on every request. Files are automatically and permanently deleted after 60 days via a GCS lifecycle rule. All infrastructure is provisioned in europe-west2 (London) to comply with UK data residency requirements.`;

const SECURITY_SECTIONS = [
  {
    title: 'Authentication',
    body: 'Users sign in with pre-issued email/password credentials via Firebase Authentication. Sessions use browserSessionPersistence — the ID token is discarded when the browser tab is closed. Every API call presents a Firebase ID token; the Cloud Function verifies it with the Firebase Admin SDK before taking any action.',
  },
  {
    title: 'Upload flow (direct-to-GCS)',
    body: 'The browser calls POST /generateUploadUrl with a valid ID token. The Cloud Function validates the token, generates a v4 signed GCS URL (15-minute expiry), and returns it. The browser then PUTs the file directly to Google Cloud Storage over HTTPS — the file bytes never pass through our Cloud Function servers.',
  },
  {
    title: 'Encryption',
    body: 'All data is encrypted in transit via HTTPS (TLS 1.2+). Files at rest in Google Cloud Storage are encrypted with AES-256 using Google-managed keys (GMEK) by default. No additional client-side encryption is applied, as GCS server-side encryption satisfies the requirement.',
  },
  {
    title: 'Access control',
    body: 'The GCS bucket has uniform bucket-level access with no public ACLs. Objects are not publicly readable. Admin download URLs are generated on-demand as short-lived signed URLs (15 minutes) and require a valid admin Firebase ID token to request. Admin status is verified by checking the caller\'s email against the ADMIN_EMAILS environment variable on every admin endpoint.',
  },
  {
    title: 'Data retention',
    body: 'Files are automatically and permanently deleted from Google Cloud Storage after 60 days via a GCS lifecycle rule (condition: Age = 60 days, action: Delete). No manual deletion step is required. Admins can download a file at any point within that window via the Admin Panel.',
  },
];

function SecurityTab() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(SECURITY_BLURB).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ maxWidth: 720 }}>
      {/* Technical explanation */}
      <div style={{ marginBottom: 40 }}>
        <Title level={5} style={{ fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 20 }}>
          Technical overview
        </Title>
        {SECURITY_SECTIONS.map((section) => (
          <div key={section.title} style={{
            marginBottom: 20,
            padding: '16px 20px',
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: 4,
          }}>
            <div style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '.5px',
              textTransform: 'uppercase', color: 'var(--orange)',
              marginBottom: 8,
            }}>
              {section.title}
            </div>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--mid)', lineHeight: 1.65 }}>
              {section.body}
            </p>
          </div>
        ))}
      </div>

      {/* Copyable blurb */}
      <div style={{ marginBottom: 8 }}>
        <Title level={5} style={{ fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 4 }}>
          Security blurb
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Copy this paragraph to include in client-facing documentation or security questionnaires.
        </Text>
      </div>
      <div style={{
        background: 'var(--almond)',
        border: '1px solid #e8d9b8',
        borderRadius: 4,
        padding: '16px 20px',
        position: 'relative',
      }}>
        <p style={{
          margin: 0, fontSize: 13, color: 'var(--black)',
          lineHeight: 1.7, paddingRight: 100,
        }}>
          {SECURITY_BLURB}
        </p>
        <button
          onClick={handleCopy}
          style={{
            position: 'absolute', top: 16, right: 16,
            fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
            background: copied ? '#2e7d32' : 'var(--orange)',
            color: 'var(--white)',
            border: 'none', borderRadius: 3,
            padding: '6px 14px', cursor: 'pointer',
            transition: 'background .2s',
            whiteSpace: 'nowrap',
          }}
        >
          {copied ? '✓ Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account tab
// ---------------------------------------------------------------------------

function AccountTab({ user }) {
  return (
    <div style={{ maxWidth: 420 }}>
      <Title level={5} style={{ fontFamily: "'Playfair Display', Georgia, serif", marginBottom: 4 }}>
        Change password
      </Title>
      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 24 }}>
        Enter your current password to verify your identity, then choose a new one.
      </Text>
      <ChangePassword user={user} onForgotPassword={() => signOut(auth)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminPanel root
// ---------------------------------------------------------------------------

function AdminPanel({ user }) {
  const tabItems = [
    {
      key: 'users',
      label: <span><TeamOutlined /> Users</span>,
      children: <UsersTab />,
    },
    {
      key: 'files',
      label: <span><FileOutlined /> Files</span>,
      children: <FilesTab />,
    },
    {
      key: 'security',
      label: <span><LockOutlined /> Security</span>,
      children: <SecurityTab />,
    },
    {
      key: 'account',
      label: <span><KeyOutlined /> Account</span>,
      children: <AccountTab user={user} />,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--surface)' }}>
      <AdminHeader user={user} />

      <main style={{ flex: 1, padding: '32px 24px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <Title level={3} style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          color: 'var(--black)',
          marginBottom: 4,
        }}>
          Admin Panel
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 28, fontSize: 14 }}>
          Manage client users and view uploaded files.
        </Text>

        <Tabs defaultActiveKey="users" items={tabItems} />
      </main>

      <LegalFooter />
    </div>
  );
}

export default AdminPanel;
